import { Link } from 'react-router-dom';
import { Check, Zap, ArrowUpRight, ShieldCheck } from 'lucide-react';
import React from 'react';

// Planos da campanha Founders Lifetime Pass — MPV (Meu Ponto de Venda)
const PLANS = [
  {
    id: 'starter',
    code: 'PLN-01',
    name: 'Starter',
    tagline: 'Para começar a sua presença comercial com o essencial.',
    price: '39.900',
    period: 'MT',
    subLabel: 'Pagamento Único · Acesso Vitalício',
    cta: 'Garantir Licença Starter',
    path: '/register',
    featured: false,
    badge: null,
    benefits: [
      'Ponto de Venda (POS) em tempo real',
      'Emissão de faturas, recibos e documentos VD',
      'Catálogo online / vitrine digital sincronizada',
      'Gestão de stock e inventário de produtos e serviços',
      'Gestão de clientes e histórico de compras',
      'Integração de pagamentos móveis M-Pesa / e-Mola',
      'Sem mensalidades — uma compra, acesso para sempre',
    ],
  },
  {
    id: 'professional',
    code: 'PLN-02',
    name: 'Professional',
    tagline: 'A escolha ideal para marcas que querem crescer com controlo total.',
    price: '79.900',
    period: 'MT',
    subLabel: 'Pagamento Único · Acesso Vitalício',
    cta: 'Garantir Licença Professional',
    path: '/register',
    featured: true,
    badge: 'MAIS RECOMENDADO',
    benefits: [
      'Tudo incluído no plano Starter',
      'Pipeline comercial CRM e conversão de leads',
      'Envio de propostas comerciais com link rastreável',
      'Metas comerciais e cálculo automático de comissões',
      'Análise de rentabilidade por cliente & Unit Economics',
      'Alertas de stock mínimo e baixa multicanal',
      'Programa de indicação / promotoras de marca',
      'Módulo de subscrições (membros/validades) ou KDS',
      'Suporte prioritário via WhatsApp',
    ],
  },
  {
    id: 'enterprise',
    code: 'PLN-03',
    name: 'Enterprise',
    tagline: 'Para operações maiores, múltiplas unidades e necessidades avançadas.',
    price: '129.900',
    period: 'MT',
    subLabel: 'Pagamento Único · Acesso Vitalício',
    cta: 'Garantir Licença Enterprise',
    path: '/register',
    featured: false,
    badge: null,
    benefits: [
      'Tudo incluído no plano Professional',
      'Gestão integrada de múltiplas lojas e filiais',
      'Utilizadores e pontos de venda ilimitados',
      'Módulos verticais completos (Ginásios, Microcrédito, Restauração KDS)',
      'Gestão de requisições, fornecedores e compras',
      'Relatórios e organização financeira para contabilista',
      'Gestor de conta dedicado',
      'Configuração inicial e formação de equipa incluídas',
    ],
  },
];

export const PricingSection: React.FC = () => {
  return (
    <div className="relative">
      {/* Banner de Destaque Vitalício */}
      <div className="flex items-center justify-center gap-2 mb-10 font-mono text-xs text-[#52C47C] bg-[#52C47C]/10 border border-[#52C47C]/30 py-2 px-4 max-w-xl mx-auto">
        <ShieldCheck size={16} />
        <span className="tracking-widest uppercase font-bold">
          FOUNDERS LIFETIME PASS — SEM MENSALIDADES PARA SEMPRE
        </span>
      </div>

      {/* Grid de Planos */}
      <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative flex flex-col p-8 border-b md:border-b-0 md:border-r last:border-r-0 border-white/10 transition-all duration-300 ${
              plan.featured
                ? 'bg-[#0e1128] border-t-2 border-t-[#52C47C]'
                : 'bg-[#12142B] hover:bg-[#0e1128]'
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-8 bg-[#52C47C] text-[#0B0C1E] font-mono font-black text-[9px] tracking-widest px-3 py-0.5 uppercase">
                {plan.badge}
              </div>
            )}

            <div className="font-mono text-[10px] text-slate-500 tracking-widest mb-4">{plan.code}</div>
            <h3 className={`text-2xl font-light mb-1 ${plan.featured ? 'text-[#52C47C]' : 'text-white'}`}>
              {plan.name}
            </h3>
            <p className="text-slate-400 text-xs font-light mb-8 leading-relaxed">{plan.tagline}</p>

            {/* Preço */}
            <div className="mb-2">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-light text-white">{plan.price}</span>
                <span className="text-slate-500 text-xs font-mono">{plan.period}</span>
              </div>
            </div>

            <div className="min-h-[20px] mb-6">
              <p className="text-[#52C47C] font-mono text-[10px] tracking-wide uppercase">{plan.subLabel}</p>
            </div>

            {/* Benefícios */}
            <ul className="space-y-3 flex-1 mb-10">
              {plan.benefits.map((b, j) => (
                <li key={j} className="flex items-start gap-3 text-xs text-slate-300 font-light">
                  <Check className="w-3.5 h-3.5 text-[#52C47C] shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <Link
              to={plan.path}
              state={{ plan: plan.id, type: 'lifetime' }}
              className={`w-full py-3.5 font-mono text-xs font-bold uppercase tracking-widest text-center flex items-center justify-center gap-2 transition-all group ${
                plan.featured
                  ? 'bg-[#52C47C] text-[#0B0C1E] hover:bg-white'
                  : 'border border-white/20 text-white hover:border-[#52C47C] hover:text-[#52C47C]'
              }`}
            >
              {plan.featured && <Zap className="w-3 h-3 fill-current" />}
              {plan.cta}
              <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center font-mono text-[10px] text-slate-500 tracking-wider">
        PAGAMENTO ÚNICO · ACESSO VITALÍCIO · CONFIGURAÇÃO INICIAL E FORMAÇÃO INCLUÍDAS
      </div>
    </div>
  );
};

export default PricingSection;
